import { Icons } from '@/libs/components/src/lib/icons';
import { Button } from '@chiroup/components/Button';
import { Checkbox } from '@chiroup/components/form/Checkbox';
import { Input } from '@chiroup/components/form/Input';
import { trivialDownload } from '@chiroup/components/TrivialDownload';
import { ChiroUpJSON } from '@chiroup/core/functions/ChiroUpJSON';
import {
  TransactionDebugMergedAnalysis,
  TransactionDebugParams,
  TransactionDebugResult,
  TransactionDebugSourceColumns,
  TransactionDebugSources,
  TransactionDebugTimelineEntry,
} from '@chiroup/core/types/Devtools.type';
import { FormError } from '@chiroup/core/types/ErrorResponse.type';
import { CloudArrowDownIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import useDevtools from '../../../hooks/useDevtools';
import devtoolsService from '../../../services/devtools.service';

const defaultValues: TransactionDebugParams = {
  groupExpand: false,
  includePayments: false,
  options: {
    include: TransactionDebugSources.reduce(
      (a, source) => {
        a[source.key] = source.checked;

        return a;
      },
      {} as Record<string, boolean>
    ) as any,
  },
};

const formatTimestamp = (ts: number): string => {
  const d = new Date(ts);
  const pad = (n: number, len = 2) => String(n).padStart(len, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
};

const formatDelta = (ms: number): string => {
  if (ms === 0) return '';
  const abs = Math.abs(ms);
  const totalSeconds = Math.floor(abs / 1000);
  const millis = abs % 1000;
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts: string[] = [];
  if (days) {
    parts.push(`${days}d`);
  }
  if (hours) {
    parts.push(`${hours}h`);
  }
  if (minutes) {
    parts.push(`${minutes}m`);
  }
  if (seconds || (!parts.length && !millis)) {
    parts.push(`${seconds}s`);
  }
  if (millis) {
    parts.push(`${millis}ms`);
  }
  return `+${parts.join(' ')}`;
};

const BK_COLORS = [
  {
    bg: 'bg-indigo-100',
    border: 'border-indigo-400',
    text: 'text-indigo-800',
    left: 'border-l-indigo-400',
  },
  {
    bg: 'bg-amber-100',
    border: 'border-amber-400',
    text: 'text-amber-800',
    left: 'border-l-amber-400',
  },
  {
    bg: 'bg-emerald-100',
    border: 'border-emerald-400',
    text: 'text-emerald-800',
    left: 'border-l-emerald-400',
  },
  {
    bg: 'bg-rose-100',
    border: 'border-rose-400',
    text: 'text-rose-800',
    left: 'border-l-rose-400',
  },
  {
    bg: 'bg-cyan-100',
    border: 'border-cyan-400',
    text: 'text-cyan-800',
    left: 'border-l-cyan-400',
  },
  {
    bg: 'bg-violet-100',
    border: 'border-violet-400',
    text: 'text-violet-800',
    left: 'border-l-violet-400',
  },
  {
    bg: 'bg-orange-100',
    border: 'border-orange-400',
    text: 'text-orange-800',
    left: 'border-l-orange-400',
  },
  {
    bg: 'bg-teal-100',
    border: 'border-teal-400',
    text: 'text-teal-800',
    left: 'border-l-teal-400',
  },
];

const buildBkColorMap = (
  entries: TransactionDebugTimelineEntry[]
): Record<string, (typeof BK_COLORS)[0]> => {
  const map: Record<string, (typeof BK_COLORS)[0]> = {};
  let idx = 0;
  for (const entry of entries) {
    if (entry.billingKey && !map[entry.billingKey]) {
      map[entry.billingKey] = BK_COLORS[idx % BK_COLORS.length];
      idx++;
    }
  }
  return map;
};

const TransactionDebug: React.FC = () => {
  const { get, isFetching } = useDevtools('transaction-debug');
  const [toggleAll, setToggleAll] = React.useState(false);
  const [timeline, setTimeline] = React.useState<TransactionDebugTimelineEntry[]>([]);
  const [analysis, setAnalysis] = React.useState<TransactionDebugMergedAnalysis[]>([]);
  const [exchanges, setExchanges] = React.useState<any[]>([]);
  const [clinicData, setClinicData] = React.useState<{ clinic: any; locations: any[] } | null>(
    null
  );
  const [quietAnalysis, setQuietAnalysis] = React.useState(true);
  const [downloadingId, setDownloadingId] = React.useState<string | null>(null);
  const [responseData, setResponseData] = React.useState<any>(null);
  const [sourceCounts, setSourceCounts] = React.useState<Record<string, number | null>>({});
  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<TransactionDebugParams>({ defaultValues });

  const watchedClinicId = watch('clinicId');
  const watchedSalesforceCase = watch('salesforceCase');
  const watchedMondayStory = watch('mondayStory');

  const onSubmit = (data: TransactionDebugParams) => {
    if (get) {
      get(data)
        .then(rawRes => {
          const res = rawRes as TransactionDebugResult | null;
          setResponseData(res);
          setTimeline(res?.timeline || []);
          setAnalysis(res?.analysis || []);

          // Extract record counts and exchanges from byBillingKey
          const counts: Record<string, number | null> = {};
          let exchangeData: any[] = [];

          if (res?.byBillingKey) {
            // Aggregate counts across all billingKeys
            for (const bkData of Object.values(res.byBillingKey)) {
              for (const source of TransactionDebugSources) {
                const table = (bkData as any)?.[source.key];
                if (table && 'data' in table && Array.isArray(table.data)) {
                  counts[source.key] = (counts[source.key] ?? 0) + table.data.length;
                }
              }

              const bkExchanges = (bkData as any)?.integrationExchange?.data;
              if (bkExchanges?.length) {
                exchangeData = exchangeData.concat(bkExchanges);
              }
            }

            // Top-level sources (group-level + clinic)
            for (const key of [
              'appointmentGroup',
              'appointmentGroupItem',
              'appointmentGroupLog',
              'clinic',
              'clinicLocations',
            ]) {
              const table = (res as any)?.[key];
              if (table && 'data' in table && Array.isArray(table.data)) {
                counts[key] = table.data.length;
              }
            }
          }

          setSourceCounts(counts);
          setExchanges(exchangeData);

          // Extract clinic data from the top level
          const clinicRow = (res as any)?.clinic?.data?.[0];
          const locationRows = (res as any)?.clinicLocations?.data || [];
          setClinicData(clinicRow ? { clinic: clinicRow, locations: locationRows } : null);
        })
        .catch(e => undefined);
    } else {
    }
  };

  const handleDownloadExchange = async (exchangeId: string, type: string) => {
    setDownloadingId(exchangeId);
    try {
      const res = await devtoolsService.transactionDebug.downloadExchange({
        exchangeId,
        clinicId: watchedClinicId,
        salesforceCase: watchedSalesforceCase,
        mondayStory: watchedMondayStory,
      });
      if (res?.content) {
        trivialDownload({
          content: res.content,
          name: `${type}_${exchangeId}.txt`,
        });
      }
    } catch (e) {
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="p-6">
      <form onSubmit={handleSubmit(onSubmit)}>
        <section className="bg-white border border-gray-400 inset-4 rounded-lg">
          <div className="p-4 flex flex-row justify-between">
            <h1 className="font-extrabold">Transaction Debug</h1>
            <div className="flex flex-row gap-2">
              {responseData && (
                <Button
                  text="Download JSON"
                  onClick={() => {
                    trivialDownload({
                      content: ChiroUpJSON.pretty(responseData),
                      name: `transaction-debug-${new Date().toISOString().replace(/[:.]/g, '-')}.json`,
                      mimeType: 'application/json',
                    });
                  }}
                  icon={Icons.cloudDownload}
                />
              )}
              <Button
                text="Fetch"
                disabled={isFetching}
                loading={isFetching}
                type="submit"
                icon={Icons.puzzle}
              />
            </div>
          </div>
          <div className="p-4 m-4 border border-gray-400 bg-gray-50 rounded-lg text-sm flex flex-col gap-2">
            <p>
              A summary is displayed below after fetching. Use Download JSON to save the full
              payload for offline analysis or to share with support agents. The payload contains PHI
              — delete it immediately after use.
            </p>
            <p>Complete documentation can be found:</p>
            <pre className="ml-4">docs/EHR/TRANSACTION_DEBUG.md</pre>
          </div>
          <div className="p-4 flex flex-col gap-4">
            <Controller
              name="clinicId"
              control={control}
              rules={{ required: 'Required.' }}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Clinic ID"
                  errors={
                    errors.clinicId
                      ? ({
                          message: errors.clinicId.message,
                        } as FormError)
                      : undefined
                  }
                />
              )}
            />
            <Controller
              name="billingKey"
              control={control}
              rules={{ required: 'Required.' }}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Billing Key"
                  errors={
                    errors.billingKey
                      ? ({
                          message: errors.billingKey.message,
                        } as FormError)
                      : undefined
                  }
                />
              )}
            />
            <Controller
              name="salesforceCase"
              control={control}
              rules={{ required: 'Required.' }}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Salesforce Case Number"
                  errors={
                    errors.salesforceCase
                      ? ({
                          message: errors.salesforceCase.message,
                        } as FormError)
                      : undefined
                  }
                />
              )}
            />
            <Controller
              name="mondayStory"
              control={control}
              rules={{ required: 'Required.' }}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Monday Story Number"
                  errors={
                    errors.mondayStory
                      ? ({
                          message: errors.mondayStory.message,
                        } as FormError)
                      : undefined
                  }
                />
              )}
            />
            <div className="mt-4 flex flex-col gap-2">
              <Controller
                name="groupExpand"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    value={field?.value}
                    onChange={field.onChange}
                    label="Pull all data for entire group appointment."
                  />
                )}
              />
              <Controller
                name="includePayments"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    value={field?.value}
                    onChange={field.onChange}
                    label="Include payments."
                  />
                )}
              />
            </div>
            <div className="mt-8">
              <div className="flex justify-between flex-row">
                <h2 className="font-extrabold">Include Options</h2>
                <div className="space-x-4 flex flex-row">
                  <Button
                    onClick={() => {
                      const tf = toggleAll;
                      setToggleAll(!toggleAll);
                      const newInclude = TransactionDebugSources.reduce(
                        (a, source) => {
                          a[source.key] = !tf;

                          return a;
                        },
                        {} as Record<string, boolean>
                      );
                      setValue('options.include', newInclude);
                    }}
                    text={`Toggle All`}
                  />
                  <Button
                    text="Reset"
                    onClick={() => {
                      setValue('options.include', defaultValues?.options?.include as any);
                    }}
                  />
                </div>
              </div>
              <div className="mt-4 flex flex-row">
                {TransactionDebugSourceColumns.map((col: any, idx) => (
                  <div key={`col-idx-${idx}`} className="w-1/4">
                    {col.map((source: any) => {
                      const count = sourceCounts[source.key];
                      const hasCounts = Object.keys(sourceCounts).length > 0;
                      return (
                        <div key={source.key} className="p-2">
                          <Controller
                            name={
                              `options.include.${source.key}` as keyof TransactionDebugParams['options']
                            }
                            control={control}
                            render={({ field }) => (
                              <Checkbox
                                value={field?.value}
                                onChange={field.onChange}
                                label={
                                  hasCounts && count != null ? (
                                    <span>
                                      {source.label}{' '}
                                      <span
                                        className={`text-xs font-mono px-1.5 py-0.5 rounded-full ${
                                          count > 0
                                            ? 'bg-primary-50 text-primary-700 ring-1 ring-primary-600'
                                            : 'bg-gray-100 text-gray-400'
                                        }`}
                                      >
                                        {count}
                                      </span>
                                    </span>
                                  ) : (
                                    source.label
                                  )
                                }
                                className="col-span-2"
                              />
                            )}
                          />
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </form>

      {clinicData && (
        <section className="mt-6 bg-white border border-gray-400 rounded-lg">
          <div className="p-4">
            <h2 className="font-extrabold">Clinic Summary</h2>
          </div>
          <div className="px-4 pb-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="font-semibold text-lg">
                  {clinicData.clinic.name || `Clinic ${clinicData.clinic.ID}`}
                </div>
                <div className="text-gray-600">
                  {[clinicData.clinic.address1, clinicData.clinic.address2]
                    .filter(Boolean)
                    .join(', ')}
                </div>
                <div className="text-gray-600">
                  {[clinicData.clinic.city, clinicData.clinic.state, clinicData.clinic.zip]
                    .filter(Boolean)
                    .join(', ')}
                </div>
                {clinicData.clinic.phone && (
                  <div className="text-gray-600">{clinicData.clinic.phone}</div>
                )}
                {clinicData.clinic.email && (
                  <div className="text-gray-600">{clinicData.clinic.email}</div>
                )}
              </div>
              <div>
                <div className="font-semibold mb-1">Locations ({clinicData.locations.length})</div>
                {clinicData.locations.map((loc: any) => (
                  <div key={loc.ID} className="mb-2 text-gray-600">
                    <div className="font-medium text-gray-800">
                      {loc.name || 'Unnamed'}{' '}
                      {loc.primary && (
                        <span className="text-xs bg-primary-50 text-primary-700 ring-1 ring-primary-600 px-1.5 py-0.5 rounded-full">
                          Primary
                        </span>
                      )}
                      {!loc.active && (
                        <span className="text-xs bg-red-50 text-red-700 ring-1 ring-red-600 px-1.5 py-0.5 rounded-full ml-1">
                          Inactive
                        </span>
                      )}
                    </div>
                    <div>
                      {[loc.address1, loc.city, loc.state, loc.zip].filter(Boolean).join(', ')}
                    </div>
                    {loc.phone && <div>{loc.phone}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {exchanges.length > 0 && (
        <section className="mt-6 bg-white border border-gray-400 rounded-lg">
          <div className="p-4">
            <h2 className="font-extrabold">Integration Exchanges ({exchanges.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="border-b border-gray-300 bg-gray-50">
                  <th className="px-3 py-2 text-left">Exchange ID</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Disposition</th>
                  <th className="px-3 py-2 text-left">Source</th>
                  <th className="px-3 py-2 text-left">Target</th>
                  <th className="px-3 py-2 text-right">Bytes</th>
                  <th className="px-3 py-2 text-left">Time</th>
                  <th className="px-3 py-2 text-left"></th>
                </tr>
              </thead>
              <tbody>
                {exchanges.map(ex => (
                  <tr key={ex.exchangeId} className="border-b border-gray-100">
                    <td className="px-3 py-1 align-top">{ex.exchangeId}</td>
                    <td className="px-3 py-1 align-top whitespace-nowrap">{ex.type}</td>
                    <td className="px-3 py-1 align-top whitespace-nowrap">{ex.disposition}</td>
                    <td className="px-3 py-1 align-top whitespace-nowrap">{ex.source}</td>
                    <td className="px-3 py-1 align-top whitespace-nowrap">{ex.target}</td>
                    <td className="px-3 py-1 align-top text-right">
                      {ex.bytes?.toLocaleString() ?? ''}
                    </td>
                    <td className="px-3 py-1 align-top whitespace-nowrap">
                      {ex.ts ? formatTimestamp(ex.ts) : ''}
                    </td>
                    <td className="px-3 py-1 align-top w-10">
                      <button
                        type="button"
                        disabled={downloadingId === ex.exchangeId || !ex.bytes}
                        onClick={() => handleDownloadExchange(ex.exchangeId, ex.type)}
                        className="inline-flex items-center justify-center w-5 h-5 text-primary-500 hover:text-primary-600 disabled:text-gray-300 disabled:cursor-not-allowed"
                        title="Download exchange file"
                      >
                        {downloadingId === ex.exchangeId ? (
                          <svg
                            className="animate-spin w-5 h-5"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                        ) : (
                          <CloudArrowDownIcon className="w-5 h-5" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {timeline.length > 0 &&
        (() => {
          const bkColors = buildBkColorMap(timeline);
          const bkEntries = Object.entries(bkColors);
          return (
            <section className="mt-6 bg-white border border-gray-400 rounded-lg">
              <div className="p-4 flex flex-row items-center justify-between">
                <h2 className="font-extrabold">Timeline ({timeline.length} events)</h2>
                {bkEntries.length > 0 && (
                  <div className="flex flex-row gap-3 flex-wrap">
                    {bkEntries.map(([bk, color]) => (
                      <span
                        key={bk}
                        className={`px-2 py-0.5 rounded text-xs font-mono ${color.bg} ${color.text} border ${color.border}`}
                      >
                        {bk.substring(0, 8)}...
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs font-mono">
                  <thead>
                    <tr className="border-b border-gray-300 bg-gray-50">
                      <th className="w-1"></th>
                      <th className="px-3 py-2 text-left">Time</th>
                      <th className="px-3 py-2 text-right">Delta</th>
                      <th className="px-3 py-2 text-left">Source</th>
                      <th className="px-3 py-2 text-left">Event</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timeline.map((entry, idx) => {
                      const bkColor = entry.billingKey ? bkColors[entry.billingKey] : null;
                      const fullEvent = entry.detail
                        ? `${entry.event}: ${entry.detail}`
                        : entry.event;
                      const eventParts = fullEvent.split('; ');
                      const isList = eventParts.length > 1;
                      return (
                        <tr
                          key={`tl-${idx}`}
                          className={`border-b border-gray-100 ${
                            entry.source === 'Invoice.statusData'
                              ? 'bg-blue-50'
                              : entry.source === 'TransactionActivity' ||
                                  entry.source === 'AppointmentActivity'
                                ? 'bg-green-50'
                                : ''
                          }`}
                        >
                          <td
                            className={`w-1 align-top ${bkColor ? `border-l-4 ${bkColor.left}` : ''}`}
                          ></td>
                          <td className="px-3 py-1 whitespace-nowrap align-top">
                            {formatTimestamp(entry.timestamp)}
                          </td>
                          <td className="px-3 py-1 whitespace-nowrap text-right text-gray-500 align-top">
                            {idx === 0
                              ? ''
                              : formatDelta(entry.timestamp - timeline[idx - 1].timestamp)}
                          </td>
                          <td className="px-3 py-1 whitespace-nowrap align-top">{entry.source}</td>
                          <td className="px-3 py-1 align-top">
                            {isList ? (
                              <ul className="list-disc list-inside">
                                {eventParts.map((part, pi) => (
                                  <li key={pi}>{part}</li>
                                ))}
                              </ul>
                            ) : (
                              fullEvent
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })()}

      {analysis.length > 0 &&
        (() => {
          const bkColors = buildBkColorMap(timeline);
          const quietMessages = [
            'Expected a single record, found one.',
            'Single record found.',
            'No records found.',
            'No record found.',
          ];
          const filtered = quietAnalysis
            ? analysis.filter(a => !quietMessages.includes(a.message))
            : analysis;
          return (
            <section className="mt-6 bg-white border border-gray-400 rounded-lg">
              <div className="p-4 flex flex-row items-center justify-between">
                <h2 className="font-extrabold">Analysis ({filtered.length} items)</h2>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={quietAnalysis}
                    onChange={e => setQuietAnalysis(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  Quiet
                </label>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs font-mono">
                  <thead>
                    <tr className="border-b border-gray-300 bg-gray-50">
                      <th className="w-1"></th>
                      <th className="px-3 py-2 text-left">Source</th>
                      <th className="px-3 py-2 text-left">Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((entry, idx) => {
                      const bkColor = entry.billingKey ? bkColors[entry.billingKey] : null;
                      return (
                        <tr
                          key={`an-${idx}`}
                          className={`border-b border-gray-100 ${
                            entry.source === 'timeline' ? 'bg-yellow-50 font-semibold' : ''
                          }`}
                        >
                          <td
                            className={`w-1 align-top ${bkColor ? `border-l-4 ${bkColor.left}` : ''}`}
                          ></td>
                          <td className="px-3 py-1 whitespace-nowrap align-top">{entry.source}</td>
                          <td className="px-3 py-1 align-top">{entry.message}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })()}
    </div>
  );
};

export default TransactionDebug;
